import { ReactNode, useCallback } from 'react';


import { cx } from '@libs/classnames';

import styles from './Tabs.module.css';
import { CheckIcon } from '@ui/icons/CheckIcon';

export type Tab<T> = {
  value: T;
  label: ReactNode;
  disabled?: boolean;
};

export type TabsProps<T> = {
  className?: string;
  tabClassName?: string;
  tabs: Tab<T>[];
  value?: T;
  disabled?: boolean;
  noTabMinWidth?: boolean;
  flat?: boolean;
  heavy?: boolean;
  preset?: 'default' | 'landing' | 'checked';
  stretch?: boolean;
  onChange: (newValue: T) => void;
};
export function Tabs<T extends string | boolean | number>({
  className,
  tabClassName,
  tabs,
  value,
  disabled,
  noTabMinWidth,
  flat,
  heavy,
  preset = 'default',
  stretch,
  onChange,
}: TabsProps<T>) {
  return (
    <div
      className={cx(
        className,
        styles.root,
        {
          [styles.no_tab_min_width]: noTabMinWidth,
          [styles.flat]: flat,
          [styles.heavy]: heavy,
          [styles.stretch]: stretch,
        },
        styles[`preset_${preset}`],
      )}
    >
      {tabs.map((t) => (
        <Tab
          key={t.value.toString()}
          className={tabClassName}
          active={value === t.value}
          label={t.label}
          value={t.value}
          onChange={onChange}
          disabled={t.disabled || disabled}
          preset={preset}
        />
      ))}
    </div>
  );
}

type TabProps<T> = {
  className?: string;
  label: ReactNode;
  value: T;
  active: boolean;
  onChange: (newValue: T) => void;
  disabled?: boolean;
  children?: ReactNode;
  preset?: 'default' | 'landing' | 'checked';
};
function Tab<T>({ className, label, value, active, onChange, disabled, children, preset }: TabProps<T>) {
  const handleClick = useCallback(() => onChange(value), [value, onChange]);
  return (
    <div
      className={cx(
        styles.tab,
        {
          [styles.active]: active,
          [styles.disabled]: disabled,
        },
        className,
      )}
      onClick={handleClick}
    >
      {children}
      {preset === 'checked' && active && <CheckIcon className={styles.checkIcon} />}
      <span className={styles.label}>{label}</span>
    </div>
  );
}
